// @flow
import type { SaveMethod }                  from '../actions/utils/apiResources/apiResourceActions.const';
import { METHOD_DEL }                       from '../actions/utils/apiResources/apiResourceActions.const';
import {
  API_HOST,
  API_PATH,
}                                           from '../config/_entrypoint';
import AccessDeniedError                    from '../errors/AccessDeniedError';
import FetchFailedError                     from '../errors/FetchFailedError';
import SubmissionErrorWithErrorCodes        from '../errors/SubmissionErrorWithErrorCodes';
import SubmissionWasInvalidError            from '../errors/SubmissionWasInvalidError';
import UserFriendlyError                    from '../errors/UserFriendlyError';
import { selectJwtTokenStringIfNotExpired } from '../selectors/auth';
import { getStore }                         from '../store';
import fetch                                from '../utils/fetch';


const jsonLdMimeType = 'application/ld+json';


type FetchOptions = {
  headers?: Headers,
  body?: FormData | string,
  method?: string,
};


class API {

  static fetch(apiPath: string, options: FetchOptions = {}) {

    const url = apiPath.includes(API_PATH) ? API_HOST + apiPath : API_HOST + API_PATH + apiPath;

    options.headers = API._setHeaders(options.headers, options.body);

    return fetch(url, options)

      .then(response => {

        if (response.ok) {

          return response;

        }

        return API._getResponseData(response)

          .catch((previousError) => {

            const error = new Error('Unable to process request.  Please try again.');

            error.previousError = previousError;

            throw error;

          })

          .then(json => {

            if (401 === response.status) {

              throw new AccessDeniedError();

            }

            const error = json['hydra:description'] || json['description'] || response.statusText;

            // noinspection JSUnresolvedVariable
            if (!json.violations) {

              throw new Error(error);

            }

            // noinspection JSUnresolvedVariable
            const errors = json.violations.reduce(
              (errors, violation) => Object.assign(errors, {[violation.propertyPath]: violation.message}),
              {_error: error},
            );

            const errorCodes = json.violations.reduce(
              (errors, violation) => Object.assign(errors,
                {[violation.propertyPath]: (violation.payload && violation.payload.errorCode) || null}),
              {},
            );

            throw new SubmissionErrorWithErrorCodes(errors, errorCodes);

          });
      })

      .then((response) => {

        return API._getResponseData(response)

          .catch((previousError) => {

            const error = new UserFriendlyError('Unexpected response from server.  Please try again.');

            error.previousError = previousError;

            throw error;

          });

      })


      // Catch & throw any previous error so that they are all handled within the error handler of the final .then(),
      // below.  This will avoid any errors during re-rendering that occurs due to the onSuccess callback call from
      // being caught.
      .catch((error) => {

        throw error;

      })

      .then(
        // Success
        (data) => {

          return data;

        },

        // Failure
        (error) => {

          if (error instanceof UserFriendlyError) {

            throw error;

          }

          if (error instanceof SubmissionErrorWithErrorCodes) {

            throw new SubmissionWasInvalidError(error, 'Sorry.  There was a problem with the information to provided.');

          }

          const userFriendlyError = new FetchFailedError(
            'Sorry.  We were unable to fetch the information for you right now.',
          );

          userFriendlyError.previousError = error;

          throw userFriendlyError;

        },
      );

  }


  /**
   * @param {string} apiPath
   * @param {string} method
   * @param {object} data
   * @param {object} options
   *
   * @return {*}
   */
  static save(apiPath: string, method: SaveMethod, data: {}, options: FetchOptions = {}) {

    options = {
      ...options,
      method,
      body: JSON.stringify(data),
    };

    return API.fetch(apiPath, options);

  }


  /**
   * Deletes a requested resource
   */
  static delete(apiPath: string) {

    return API.fetch(apiPath, {method: METHOD_DEL});

  }


  // -----------------------------------------------------------------------------------------------------------------
  // 'Private' methods
  // -----------------------------------------------------------------------------------------------------------------

  /**
   * Sets the headers on the given
   *
   * @private
   *
   * @param {Headers}         headers
   * @param {FormData|string} body
   *
   * @return {Headers}
   */
  static _setHeaders(headers?: Headers = new Headers(), body?: FormData | string): Headers {

    if (null === headers.get('Accept')) {

      headers.set('Accept', jsonLdMimeType);

    }

    if (
      'undefined' !== body
      && !(body instanceof FormData)
      && null === headers.get('Content-Type')
    ) {
      headers.set('Content-Type', jsonLdMimeType);
    }

    const token = selectJwtTokenStringIfNotExpired(getStore().getState());

    if (token) {
      headers.set('Authorization', 'Bearer ' + token);
    }

    return headers;

  }


  /**
   * Returns a resolved Promise returning the JSON decoded response, unless the response code is 204 (no content), in
   * which case, the promise resolves, returning `null`.
   *
   * @private
   *
   * @param {Response} response
   *
   * @return {Promise<null | {}>}
   */
  static _getResponseData(response: Response): Promise<null | {}> {

    // 204 = No content, so don't JSON decode
    if (204 === response.status) {
      return Promise.resolve(null);
    }

    return response.json();

  }

}


export default API;


// WEBPACK FOOTER //
// ./src/services/API.js